import { ReloadOutlined } from "@ant-design/icons";
import { Button, DatePicker, Select, Spin, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import CommonIcons from "../../../../assets/icons";
import
{
   deliveredAtColumn,
   orderNameColumn,
   orderNumberColumn,
} from "../../../../components/VMRS/Order/Tasking/orderColumns";
import KeywordSearchInput from "../../../../components/VMRS/Order/Tasking/KeywordSearchInput";
import { OrderItem } from "../../../../components/VMRS/Order/Tasking/types/order";
import
{
   DELIVERED_FROM_PARAM,
   DELIVERED_SORT_FIELD,
   DELIVERED_TO_PARAM,
   KEYWORD_PARAM,
   resolveTableSort,
   sumColumnWidths,
} from "../../../../components/VMRS/Order/Tasking/ultis";
import { useFetchDataRealtimeSync } from "../../../../components/VMRS/Order/hooks/useFetchDataRealtime";
import { monitoringOrderPaths } from "../../../../constants/routePaths";
import useMenuQueryFilter from "../../../../hooks/useMenuQueryFilter";
import ContentLayoutManagement from "../../../../layouts/ContentLayoutManagement";
import HeaderContent from "../../../../layouts/HeaderContent";
import { getOrderAsync } from "../../../../store/reducers/orderSlice";
import { RootState } from "../../../../store/store";

const ALL_VALUE = "all";

// Tags shown before the rest collapse into a "+N" tag.
const CATEGORY_TAG_LIMIT = 2;

// `tag-select` is meant for tags inside a Select: full width, centred, and filled with
// the table's own background — so the chips here are styled on their own.
const CATEGORY_TAG_CLASS =
   "!m-0 !px-3 !py-[1px] !w-auto !rounded !border-none " +
   "!bg-[var(--neutral-700)] !text-[var(--text-primary)]";

/** An order can carry one category or several, under either name. */
const getCategories = (item: any): any[] =>
{
   const raw =
      item?.order_categories ??
      item?.categories ??
      item?.order_category ??
      item?.category;

   if (!raw) return [];

   return Array.isArray(raw) ? raw : [raw];
};

const getCategoryLabel = (category: any): string =>
   category?.name ?? category?.name_vi ?? category?.code ?? "";

// The menu row for this page ships its route under `extra.route` while the row's own
// `path` says something else, so both are registered and both are matched here.
const MENU_PATHS = [monitoringOrderPaths.path, monitoringOrderPaths.aliasPath];

export default function MonitoringOrderListPage()
{
   const navigate = useNavigate();
   const dispatch = useDispatch();
   const { t } = useTranslation();
   const [searchParams, setSearchParams] = useSearchParams();

   const [data, setData] = useState<OrderItem[]>([]);
   const [total, setTotal] = useState(0);
   // Starts true: the first request waits on the menu, so the table would otherwise
   // flash "no data" before the filter is even known.
   const [loading, setLoading] = useState(true);

   const notifications = useSelector(
      (state: RootState) => state.notificationSlice.total,
   );

   const providers = useSelector(
      (state: RootState) => state.appCommonSlice.allProviders,
   );

   const dataStatusOrder = useSelector(
      (state: RootState) => state.appCommonSlice.dataStatusOrder,
   );

   // The menu row pins this page to `order_categories=[2, 3, 4]` — the param name comes
   // from its `query_filter`, the categories from the row field of that same name, and
   // they double as the options of the category filter below. Undefined until the menu
   // has loaded, which is what holds back the first request.
   const menuFilter = useMenuQueryFilter(MENU_PATHS);

   const categoryParam = menuFilter?.param;

   const categoryOptions = (menuFilter?.entries ?? []).map((category: any) => ({
      value: String(category?.id ?? category?.code),
      label: getCategoryLabel(category),
   }));

   // ================= PARSE URL =================
   const parseFiltersFromURL = (params: URLSearchParams) =>
   {
      return {
         page: Number(params.get("page") || 0),
         pageSize: Number(params.get("size") || 10),
         sort: (params.get("sort") as "asc" | "desc") || "desc",
         // Absent means the endpoint's default field (the order date).
         sortBy: params.get("sort_by") || null,
         providerIds: params.get("providers")
            ? params.get("providers")!.split(",")
            : ["all"],
         statusIds: params.get("statusIds")
            ? params.get("statusIds")!.split(",")
            : ["all"],
         categoryIds: params.get("categories")
            ? params.get("categories")!.split(",")
            : ["all"],
         startDate: params.get("startDate")
            ? Number(params.get("startDate"))
            : null,
         endDate: params.get("endDate") ? Number(params.get("endDate")) : null,
         searchName: params.get("name") || "",
         keyword: params.get("keyword") || "",
         deliveredStart: params.get("deliveredStart")
            ? Number(params.get("deliveredStart"))
            : null,
         deliveredEnd: params.get("deliveredEnd")
            ? Number(params.get("deliveredEnd"))
            : null,
      };
   };

   // ================= STATE =================
   const [filters, setFilters] = useState(() =>
      parseFiltersFromURL(searchParams),
   );

   const {
      page,
      pageSize,
      sort,
      sortBy,
      providerIds,
      statusIds,
      categoryIds,
      startDate,
      endDate,
      searchName,
      keyword,
      deliveredStart,
      deliveredEnd,
   } = filters;

   // ================= SYNC URL =================

   useEffect(() =>
   {
      const params = new URLSearchParams();

      if (filters.page > 0) params.set("page", String(filters.page));
      if (filters.pageSize !== 10) params.set("size", String(filters.pageSize));
      if (filters.sort !== "desc") params.set("sort", filters.sort);
      if (filters.sortBy) params.set("sort_by", filters.sortBy);

      if (!filters.providerIds.includes("all"))
      {
         params.set("providers", filters.providerIds.join(","));
      }

      if (!filters.statusIds.includes("all"))
      {
         params.set("statusIds", filters.statusIds.join(","));
      }

      if (!filters.categoryIds.includes("all"))
      {
         params.set("categories", filters.categoryIds.join(","));
      }

      if (filters.startDate) params.set("startDate", String(filters.startDate));
      if (filters.endDate) params.set("endDate", String(filters.endDate));
      if (filters.searchName) params.set("name", filters.searchName);
      if (filters.keyword) params.set("keyword", filters.keyword);

      if (filters.deliveredStart)
      {
         params.set("deliveredStart", String(filters.deliveredStart));
      }

      if (filters.deliveredEnd)
      {
         params.set("deliveredEnd", String(filters.deliveredEnd));
      }

      setSearchParams(params, { replace: true });
   }, [filters]);

   const updateFilters = (newPartial: Partial<typeof filters>) =>
   {
      setFilters((prev) => ({
         ...prev,
         ...newPartial,
         page: 0,
      }));
   };
   // ================= API =================
   const getData = async () =>
   {
      // Fetching before the menu resolves would list every order, unfiltered.
      if (!menuFilter) return;

      // "All" means every category the menu pinned to the page, not "no filter".
      const categoryFilter = categoryParam
         ? {
            [categoryParam]: categoryIds.includes(ALL_VALUE)
               ? menuFilter.values
               : categoryIds,
         }
         : {};

      try
      {
         setLoading(true);

         // @ts-ignore
         const res = await dispatch(
            // @ts-ignore
            getOrderAsync({
               ...categoryFilter,
               order_type: "tasking",
               has_category: true,
               page: page,
               limit: pageSize,
               sort,
               sort_by: sortBy || undefined,
               provider_ids: providerIds.includes("all") ? undefined : providerIds,
               status_ids: statusIds?.includes("all") ? undefined : statusIds,
               start_date: startDate ? Math.floor(startDate / 1000) : undefined,
               end_date: endDate ? Math.floor(endDate / 1000) : undefined,
               name: searchName || undefined,
               [KEYWORD_PARAM]: keyword || undefined,
               [DELIVERED_FROM_PARAM]: deliveredStart
                  ? Math.floor(deliveredStart / 1000)
                  : undefined,
               [DELIVERED_TO_PARAM]: deliveredEnd
                  ? Math.floor(deliveredEnd / 1000)
                  : undefined,
            }),
         ).unwrap();

         setData(res?.data?.data || []);
         setTotal(res?.data?.pagination?.total || 0);
      } catch
      {
         setData([]);
         setTotal(0);
      } finally
      {
         setLoading(false);
      }
   };

   useEffect(() =>
   {
      getData();
   }, [
      page,
      pageSize,
      sort,
      sortBy,
      startDate,
      endDate,
      searchName,
      keyword,
      deliveredStart,
      deliveredEnd,
      providerIds.join(","),
      statusIds.join(","),
      categoryIds.join(","),
      JSON.stringify(menuFilter?.params ?? null),
   ]);

   // ================= HANDLE PROVIDER AND STATUS =================

   const handleProviderChange = (vals: string[]) =>
   {
      if (!providers) return;

      const allIds = providers.map((p) => p.id.toString());

      const hasAll = vals.includes(ALL_VALUE);

      // Case 0: clear sạch → về mặc định ALL thay vì bộ lọc rỗng.
      if (!vals.length)
      {
         updateFilters({ providerIds: [ALL_VALUE, ...allIds] });
         return;
      }

      // Case 1: click ALL từ trạng thái không ALL
      if (hasAll && !providerIds.includes(ALL_VALUE))
      {
         updateFilters({ providerIds: [ALL_VALUE, ...allIds] });
         return;
      }

      // Case 2: đang ALL mà user bỏ bớt item
      if (providerIds.includes(ALL_VALUE) && hasAll)
      {
         const cleaned = vals.filter((v) => v !== ALL_VALUE);

         // nếu không còn full nữa → bỏ ALL
         if (cleaned.length !== allIds.length)
         {
            updateFilters({ providerIds: cleaned });
            return;
         }

         // vẫn full thì giữ ALL
         updateFilters({ providerIds: [ALL_VALUE, ...allIds] });

         return;
      }

      //  Case 3: select bình thường
      const cleaned = vals.filter((v) => v !== ALL_VALUE);

      // nếu user select full bằng tay → add ALL
      if (cleaned.length === allIds.length)
      {
         updateFilters({ providerIds: [ALL_VALUE, ...allIds] });
         return;
      }
      updateFilters({ providerIds: cleaned });
   };

   // ================= RESET =================

   const handleStatusChange = (vals: string[]) =>
   {
      if (!dataStatusOrder) return;

      const allIds = dataStatusOrder.map((s: any) => s.id.toString());

      const prevHasAll = statusIds.includes(ALL_VALUE);
      const hasAll = vals.includes(ALL_VALUE);

      //  Case 1: click ALL (bật ALL)
      if (!prevHasAll && hasAll)
      {
         updateFilters({ statusIds: [ALL_VALUE, ...allIds] });
         return;
      }

      //  Case 1b: clear sạch → về mặc định ALL thay vì bộ lọc rỗng.
      if (!vals.filter(v => v !== ALL_VALUE).length)
      {
         updateFilters({ statusIds: [ALL_VALUE, ...allIds] });
         return;
      }

      //  Case 2: bỏ ALL
      if (prevHasAll && !hasAll)
      {
         const cleaned = vals.filter(v => v !== ALL_VALUE);
         updateFilters({ statusIds: cleaned });
         return;
      }

      // Case 3: bình thường
      const cleaned = vals.filter(v => v !== ALL_VALUE);

      // nếu select đủ hết → auto ALL
      if (cleaned.length === allIds.length)
      {
         updateFilters({ statusIds: [ALL_VALUE, ...allIds] });
         return;
      }

      updateFilters({ statusIds: cleaned });
   };
   const handleCategoryChange = (vals: string[]) =>
   {
      if (!categoryOptions.length) return;

      const allIds = categoryOptions.map((option) => option.value);

      const prevHasAll = categoryIds.includes(ALL_VALUE);
      const hasAll = vals.includes(ALL_VALUE);
      const cleaned = vals.filter((v) => v !== ALL_VALUE);

      // Case 1: bật ALL
      if (!prevHasAll && hasAll)
      {
         updateFilters({ categoryIds: [ALL_VALUE, ...allIds] });
         return;
      }

      // Case 2: clear sạch → về mặc định ALL. Để trống thì request không gửi
      // order_categories nữa và list sẽ lọt cả đơn ngoài các danh mục của menu.
      if (!cleaned.length)
      {
         updateFilters({ categoryIds: [ALL_VALUE, ...allIds] });
         return;
      }

      // Case 3: bỏ ALL nhưng vẫn còn chọn tay
      if (prevHasAll && !hasAll)
      {
         updateFilters({ categoryIds: cleaned });
         return;
      }

      // Case 4: bình thường — chọn đủ hết thì tự bật lại ALL
      updateFilters({
         categoryIds:
            cleaned.length === allIds.length ? [ALL_VALUE, ...allIds] : cleaned,
      });
   };

   const handleResetFilters = () =>
   {
      const { origin, pathname } = window.location;

      // full path không có query
      const fullPath = origin + pathname;

      // href hiện tại (có query)
      const currentHref = window.location.href;

      // nếu href chỉ khác mỗi query (tức là cùng path)
      if (currentHref === fullPath)
      {
         getData();

         return;
      }
      setFilters({
         page: 0,
         pageSize: 10,
         sort: "desc",
         sortBy: null,
         providerIds: ["all"],
         statusIds: ["all"],
         categoryIds: ["all"],
         startDate: null,
         endDate: null,
         searchName: "",
         keyword: "",
         deliveredStart: null,
         deliveredEnd: null,
      });
   };

   const columns: ColumnsType<OrderItem> = [
      {
         title: t("No."),
         key: "index",
         width: 60,
         render: (_: any, __: any, index: number) => page * pageSize + index + 1,
         align: "center",
      },
      orderNumberColumn(t),
      orderNameColumn(t),
      {
         title: t("Provider"),
         key: "provider",
         render: (item: OrderItem) =>
         {
            return <span>{item?.items?.[0]?.provider?.name || "N/A"}</span>;
         },
         width: 130,
      },

      {
         title: t("Category"),
         key: "order_categories",
         render: (item: OrderItem) =>
         {
            const categories = getCategories(item);

            if (!categories.length) return <span className="opacity-60">--</span>;

            const shown = categories.slice(0, CATEGORY_TAG_LIMIT);
            const hidden = categories.slice(CATEGORY_TAG_LIMIT);

            return (
               <div className="flex flex-col items-start gap-1">
                  {shown.map((category: any, index: number) => (
                     <Tag
                        key={category?.id ?? category?.code ?? index}
                        className={CATEGORY_TAG_CLASS}
                     >
                        {getCategoryLabel(category)}
                     </Tag>
                  ))}

                  {hidden.length > 0 && (
                     <Tooltip
                        styles={{ root: { pointerEvents: "none" } }}
                        title={hidden.map(getCategoryLabel).join(", ")}
                     >
                        <Tag className={CATEGORY_TAG_CLASS}>
                           + {hidden.length}
                        </Tag>
                     </Tooltip>
                  )}
               </div>
            );
         },
         width: 180,
      },

      {
         title: t("Ordered at"),
         dataIndex: "created_at",
         key: "created_at",
         render: (timestamp: number) =>
            dayjs(timestamp).format("HH:mm DD/MM/YYYY"),
         width: 180,
         sorter: true,
         // Sorted by the order date whenever no other field is asked for.
         sortOrder: sortBy ? null : sort === "asc" ? "ascend" : "descend",
      },

      deliveredAtColumn(
         t,
         sortBy === DELIVERED_SORT_FIELD
            ? sort === "asc"
               ? "ascend"
               : "descend"
            : null,
      ),

      {
         title: t("Status"),
         key: "status",
         render: (item: OrderItem) => (
            <Tag
               color={item?.status?.color}
               className="px-3 py-1 rounded-full !text-white !min-w-[150px] flex !justify-center"
            >
               {t(item?.status?.name)}
            </Tag>
         ),
         width: 200,
      },
      {
         title: t("Priority"),
         key: "priority",
         render: (item: OrderItem) => <p>{t(item?.priority)}</p>,
         width: 100,
         align: "center",
      },
      {
         title: "",
         key: "action",
         align: "right",
         render: (_: any, record: any) => (
            <div className="flex items-center text-center  justify-end">
               <Button
                  type="link"
                  onClick={() =>
                     navigate({
                        pathname: `./${record.id}`,
                        search: searchParams.toString(),
                     })
                  }
               >
                  {t("Detail")}
               </Button>

               <Button
                  className="!w-6 min-w-6 h-6"
                  type="text"
                  icon={<CommonIcons.NextIcon />}
                  onClick={() =>
                     navigate({
                        pathname: `./${record.id}`,
                        search: searchParams.toString(),
                     })
                  }
               />
            </div>
         ),
         width: 110,
      },
   ];



   useFetchDataRealtimeSync({
      notifications,
      getData,
      path: monitoringOrderPaths.notificationTag,
   });

   const handleTableChange = (pagination: any, _: any, sorter: any) =>
   {
      setFilters((prev) => ({
         ...prev,
         page: pagination.current - 1,
         pageSize: pagination.pageSize,
         ...resolveTableSort(sorter, { sort: prev.sort, sortBy: prev.sortBy }),
      }));
   };

   // ================= RENDER =================
   return (
      <ContentLayoutManagement
         header={
            <HeaderContent breadcrumb={[{ title: t("Monitoring Orders") }]} />
         }
      >
         <Spin spinning={loading}>
            <div className="p-6 bg-[#0f172a] h-full overflow-hidden text-white flex flex-col">
               {/* Filters */}
               <div className="flex flex-wrap gap-3 mb-4 w-full">
                  <div className="flex-1 min-w-[180px]">
                     <p className="mb-1" style={{ fontSize: "14px" }}>
                        {t("Order number or name")}
                     </p>
                     <KeywordSearchInput
                        value={keyword}
                        disabled={loading}
                        onSearch={(next) => updateFilters({ keyword: next })}
                     />
                  </div>

                  <div className="flex-1 min-w-[180px]">
                     <p style={{ fontSize: "14px" }} className="mb-1">
                        {t("Provider")}
                     </p>
                     <Select
                        disabled={loading}
                        mode="multiple"
                        value={providerIds}
                        onChange={handleProviderChange}
                        optionFilterProp="label"
                        className="div-dropdown"
                        options={[{ value: "all", label: t("All") }].concat(
                           providers?.map((provider) => ({
                              value: provider.id.toString(),
                              label: provider.name,
                           })) || [],
                        )}
                        tagRender={(props) =>
                        {
                           const isAllSelected =
                              providerIds.includes("all") ||
                              providerIds.length === providers?.length;

                           if (isAllSelected)
                           {
                              if (props.value !== "all")
                              {
                                 return <span style={{ display: "none" }} />;
                              }

                              return (
                                 <Tag className="tag-select" closable={false}>
                                    {t("All")}
                                 </Tag>
                              );
                           }

                           return (
                              <Tag
                                 className="tag-select"
                                 closable
                                 onClose={props.onClose}
                              >
                                 {props.label}
                              </Tag>
                           );
                        }}
                     />
                  </div>

                  <div className="flex-1 min-w-[180px]">
                     <p className="mb-1" style={{ fontSize: "14px" }}>
                        {t("Status")}
                     </p>

                     <Select
                        disabled={loading}
                        mode="multiple"
                        value={statusIds}
                        maxTagCount="responsive"
                        maxTagPlaceholder={(omittedValues) => (
                           <Tooltip
                              styles={{ root: { pointerEvents: 'none' } }}
                              title={omittedValues.map(({ label }) => label).join(', ')}
                           >
                              <span>+ {omittedValues?.length}...</span>
                           </Tooltip>
                        )}
                        onChange={handleStatusChange}
                        optionFilterProp="label"
                        className="div-dropdown"
                        options={[{ value: "all", label: t("All") }].concat(
                           dataStatusOrder?.map((item: any) => ({
                              value: item.id.toString(),
                              label: t(item.name),
                           })) || [],
                        )}
                        tagRender={(props) =>
                        {
                           const isAllSelected =
                              statusIds.includes("all") ||
                              statusIds.length === dataStatusOrder?.length;

                           if (isAllSelected)
                           {
                              if (props.value !== "all")
                              {
                                 return <span style={{ display: "none" }} />;
                              }

                              return (
                                 <Tag className="tag-select" closable={false}>
                                    {t("All")}
                                 </Tag>
                              );
                           }

                           return (
                              <Tag
                                 className="tag-select"
                                 closable
                                 onClose={props.onClose}
                              >
                                 {props.label}
                              </Tag>
                           );
                        }}
                     />
                  </div>

                  <div className="flex-1 min-w-[180px]">
                     <p className="mb-1" style={{ fontSize: "14px" }}>
                        {t("Category")}
                     </p>

                     <Select
                        disabled={loading || !categoryOptions.length}
                        mode="multiple"
                        value={categoryIds}
                        maxTagCount="responsive"
                        maxTagPlaceholder={(omittedValues) => (
                           <Tooltip
                              styles={{ root: { pointerEvents: "none" } }}
                              title={omittedValues
                                 .map(({ label }) => label)
                                 .join(", ")}
                           >
                              <span>+ {omittedValues?.length}...</span>
                           </Tooltip>
                        )}
                        onChange={handleCategoryChange}
                        optionFilterProp="label"
                        className="div-dropdown"
                        options={[{ value: ALL_VALUE, label: t("All") }].concat(
                           categoryOptions,
                        )}
                        tagRender={(props) =>
                        {
                           const isAllSelected =
                              categoryIds.includes(ALL_VALUE) ||
                              categoryIds.length === categoryOptions.length;

                           if (isAllSelected)
                           {
                              if (props.value !== ALL_VALUE)
                              {
                                 return <span style={{ display: "none" }} />;
                              }

                              return (
                                 <Tag className="tag-select" closable={false}>
                                    {t("All")}
                                 </Tag>
                              );
                           }

                           return (
                              <Tag
                                 className="tag-select"
                                 closable
                                 onClose={props.onClose}
                              >
                                 {props.label}
                              </Tag>
                           );
                        }}
                     />
                  </div>

                  <div className="flex-1 min-w-[320px]">
                     <p className="mb-1" style={{ fontSize: "14px" }}>
                        {t("Created at")}
                     </p>
                     <div className="flex gap-3">
                        <DatePicker
                           value={startDate ? dayjs(startDate) : null}
                           onChange={(date) =>


                              updateFilters({
                                 startDate: date ? date.startOf("day").valueOf() : null,
                              })
                           }
                           placeholder={t("Start date")}
                           className=" div-dropdown"
                           format="DD/MM/YYYY"
                           disabledDate={(current) =>
                           {
                              if (!current) return false;

                              const isFuture = current > dayjs().endOf("day");
                              const isAfterEnd =
                                 !!endDate && current > dayjs(endDate).endOf("day");

                              return isFuture || isAfterEnd;
                           }}
                           disabled={loading}
                        />
                        <DatePicker
                           value={endDate ? dayjs(endDate) : null}
                           onChange={(date) =>
                           {
                              if (!date)
                              {
                                 setFilters((prev) => ({
                                    ...prev,
                                    endDate: null,
                                    page: 0,
                                 }));
                                 return;
                              }

                              const isToday = date.isSame(dayjs(), "day");
                              updateFilters({
                                 endDate: isToday
                                    ? dayjs().valueOf()
                                    : date.endOf("day").valueOf(),
                              });
                           }}
                           placeholder={t("End date")}
                           className="div-dropdown"
                           format="DD/MM/YYYY"
                           disabledDate={(current) =>
                           {
                              if (!current) return false;

                              const isFuture = current > dayjs().endOf("day");
                              const isBeforeStart =
                                 !!startDate && current < dayjs(startDate).startOf("day");

                              return isFuture || isBeforeStart;
                           }}
                           disabled={loading}
                        />
                     </div>
                  </div>

                  <div className="flex-1 min-w-[320px]">
                     <p className="mb-1" style={{ fontSize: "14px" }}>
                        {t("Image delivered at")}
                     </p>
                     <div className="flex gap-3">
                        <DatePicker
                           value={deliveredStart ? dayjs(deliveredStart) : null}
                           onChange={(date) =>
                              updateFilters({
                                 deliveredStart: date
                                    ? date.startOf("day").valueOf()
                                    : null,
                              })
                           }
                           placeholder={t("Start date")}
                           className="div-dropdown"
                           format="DD/MM/YYYY"
                           disabledDate={(current) =>
                           {
                              if (!current) return false;

                              // Images can't have been delivered in the future, and
                              // the start of the range can't sit past its end.
                              const isFuture = current > dayjs().endOf("day");
                              const isAfterEnd =
                                 !!deliveredEnd &&
                                 current > dayjs(deliveredEnd).endOf("day");

                              return isFuture || isAfterEnd;
                           }}
                           disabled={loading}
                        />
                        <DatePicker
                           value={deliveredEnd ? dayjs(deliveredEnd) : null}
                           onChange={(date) =>
                           {
                              if (!date)
                              {
                                 setFilters((prev) => ({
                                    ...prev,
                                    deliveredEnd: null,
                                    page: 0,
                                 }));
                                 return;
                              }

                              const isToday = date.isSame(dayjs(), "day");
                              updateFilters({
                                 deliveredEnd: isToday
                                    ? dayjs().valueOf()
                                    : date.endOf("day").valueOf(),
                              });
                           }}
                           placeholder={t("End date")}
                           className="div-dropdown"
                           format="DD/MM/YYYY"
                           disabledDate={(current) =>
                           {
                              if (!current) return false;

                              const isFuture = current > dayjs().endOf("day");
                              const isBeforeStart =
                                 !!deliveredStart &&
                                 current < dayjs(deliveredStart).startOf("day");

                              return isFuture || isBeforeStart;
                           }}
                           disabled={loading}
                        />
                     </div>
                  </div>

                  <div className="min-w-max flex flex-col mb-1">
                     {/* Keeps the button level with the inputs, which all sit
                         under a label. This list has one filter more than the
                         tasking one, so the button gives up its own label and
                         text to keep the row on a single line. */}
                     <p className="mb-1 select-none invisible" aria-hidden>
                        {t("Reset")}
                     </p>
                     <Tooltip title={t("Reset")}>
                        <Button
                           disabled={loading}
                           className="!bg-[var(--neutral-900)] !border-none text-[var(--text-primary)]"
                           icon={<ReloadOutlined />}
                           aria-label={t("Reset")}
                           onClick={handleResetFilters}
                        />
                     </Tooltip>
                  </div>
               </div>

               {/* Table */}

               <Table
                  className="flex-1 min-h-0"
                  columns={columns}
                  // Below the width the columns ask for, scroll sideways rather
                  // than squeeze every cell down to a few characters. Wider than
                  // that and AntD shows no scrollbar at all.
                  scroll={{ x: sumColumnWidths(columns) }}
                  // With scroll.x AntD would otherwise fall back to an auto
                  // layout, which lets a long order name push its column past the
                  // width it asked for instead of ellipsising inside it.
                  tableLayout="fixed"
                  dataSource={data}
                  // bordered
                  pagination={{
                     current: page + 1,
                     pageSize: pageSize,
                     total: total,
                     showTotal: (total, range) =>
                        // One sentence, one key: the old version translated only
                        // "Show" and "records", leaving "to"/"of" in English.
                        t("Showing {{from}} to {{to}} of {{total}} records", {
                           from: range[0],
                           to: range[1],
                           total,
                        }),
                     // onChange: handlePagination,
                     pageSizeOptions: ["10", "20", "50", "100"],
                  }}
                  onRow={(record, rowIndex) =>
                  {
                     return {
                        onClick: () =>
                        {
                           navigate({
                              pathname: `./${record.id}`,
                              search: searchParams.toString(),
                           });
                        },
                     };
                  }}
                  onChange={handleTableChange}
               />
            </div>
         </Spin>
      </ContentLayoutManagement>
   );
}
